import { useEffect, useState } from "react";

function useNow() {
  const [now, setNow] = useState<Date | null>(null);
  useEffect(() => {
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

// Curva base de usuários online em um dado instante.
// - Madrugada (00h–06h): ~ -60%
// - Sábado / Domingo: ~ -20%
function computeOnline(d: Date) {
  const hour = d.getHours() + d.getMinutes() / 60;
  const day = d.getDay();
  const MIN = 3450;
  const MAX = 3939;
  const range = MAX - MIN;
  // pico ~14h, vale ~03h
  const diurnal = 0.5 - 0.5 * Math.cos(((hour - 3) / 24) * Math.PI * 2);
  let value = MIN + range * (0.55 + 0.45 * diurnal);
  if (hour >= 0 && hour < 6) value *= 0.4; // -60%
  if (day === 0 || day === 6) value *= 0.8; // -20%
  // jitter determinístico por bucket de 30 min
  const bucket = Math.floor(d.getTime() / (30 * 60 * 1000));
  const jitter = ((Math.sin(bucket * 12.9898) * 43758.5453) % 1) * 60 - 30;
  return Math.max(800, Math.round(value + jitter));
}

function useOnlineUsers() {
  const [n, setN] = useState<number | null>(null);
  useEffect(() => {
    setN(computeOnline(new Date()));
    // recálculo da base a cada 30 min
    const base = setInterval(() => setN(computeOnline(new Date())), 30 * 60 * 1000);
    // micro variação a cada 5–15s (±2 a ±10 usuários)
    let micro: ReturnType<typeof setTimeout>;
    const tick = () => {
      setN((prev) => {
        if (prev == null) return prev;
        const delta = Math.floor(Math.random() * 21) - 10; // -10..+10
        return Math.max(800, prev + delta);
      });
      micro = setTimeout(tick, 5000 + Math.random() * 10000);
    };
    micro = setTimeout(tick, 5000 + Math.random() * 10000);
    return () => {
      clearInterval(base);
      clearTimeout(micro);
    };
  }, []);
  return n;
}

function buildHistory() {
  const now = new Date();
  const out: { t: Date; v: number }[] = [];
  for (let i = 23; i >= 0; i--) {
    const t = new Date(now.getTime() - i * 30 * 60 * 1000);
    out.push({ t, v: computeOnline(t) });
  }
  return out;
}

function useHistory() {
  const [pts, setPts] = useState<{ t: Date; v: number }[]>([]);
  useEffect(() => {
    setPts(buildHistory());
    const id = setInterval(() => setPts(buildHistory()), 30 * 60 * 1000);
    return () => clearInterval(id);
  }, []);
  return pts;
}

function LineChart({ data, color = "oklch(0.82 0.16 150)" }: { data: { t: Date; v: number }[]; color?: string }) {
  const W = 520;
  const H = 110;
  const PAD_X = 6;
  const PAD_Y = 10;
  if (data.length < 2) return null;
  const vals = data.map((d) => d.v);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = Math.max(1, max - min);
  const stepX = (W - PAD_X * 2) / (data.length - 1);
  const pts = data.map((d, i) => {
    const x = PAD_X + i * stepX;
    const y = PAD_Y + (H - PAD_Y * 2) * (1 - (d.v - min) / span);
    return [x, y] as const;
  });
  const path = pts.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(" ");
  const area = `${path} L${pts[pts.length - 1][0]},${H} L${pts[0][0]},${H} Z`;
  const last = pts[pts.length - 1];
  const fmt = (d: Date) => d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-28 w-full" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="sm-area" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map((p) => (
        <line key={p} x1={PAD_X} x2={W - PAD_X} y1={PAD_Y + (H - PAD_Y * 2) * p} y2={PAD_Y + (H - PAD_Y * 2) * p} stroke="oklch(0.20 0.03 260 / 0.06)" strokeWidth="1" />
      ))}
      <path d={area} fill="url(#sm-area)" />
      <path d={path} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="3" fill={color} />
      <circle cx={last[0]} cy={last[1]} r="6" fill={color} fillOpacity="0.25">
        <animate attributeName="r" values="3;9;3" dur="2.2s" repeatCount="indefinite" />
        <animate attributeName="fill-opacity" values="0.35;0;0.35" dur="2.2s" repeatCount="indefinite" />
      </circle>
      <text x={PAD_X} y={H - 1} fontSize="8" fill="oklch(0.20 0.03 260 / 0.45)">{fmt(data[0].t)}</text>
      <text x={W / 2 - 10} y={H - 1} fontSize="8" fill="oklch(0.20 0.03 260 / 0.45)">−6h</text>
      <text x={W - PAD_X - 22} y={H - 1} fontSize="8" fill="oklch(0.20 0.03 260 / 0.45)">agora</text>
    </svg>
  );
}

// Linha reta verde indicando estabilidade total do serviço
function ServiceSpark() {
  const W = 80;
  const H = 22;
  const y = H / 2;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-5 w-20" preserveAspectRatio="none" aria-hidden>
      <line x1={2} x2={W - 8} y1={y} y2={y} stroke="oklch(0.82 0.16 150 / 0.35)" strokeWidth="1" strokeDasharray="2 3" />
      <line x1={2} x2={W - 8} y1={y} y2={y} stroke="oklch(0.82 0.16 150)" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx={W - 8} cy={y} r="2" fill="oklch(0.82 0.16 150)" />
      <circle cx={W - 8} cy={y} r="4" fill="oklch(0.82 0.16 150)" fillOpacity="0.25">
        <animate attributeName="r" values="2;6;2" dur="2s" repeatCount="indefinite" />
        <animate attributeName="fill-opacity" values="0.35;0;0.35" dur="2s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

function PingDot({ color = "oklch(0.78 0.18 150)" }: { color?: string }) {
  return (
    <span className="relative inline-flex h-2.5 w-2.5">
      <span className="absolute inset-0 animate-ping rounded-full" style={{ background: color, opacity: 0.6 }} />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ background: color }} />
    </span>
  );
}

const SERVICES = [
  { name: "API CRM", latency: 38 },
  { name: "WhatsApp Cloud", latency: 64 },
  { name: "IA · Inferência", latency: 92 },
  { name: "Webhooks", latency: 41 },
  { name: "Banco de Dados", latency: 22 },
  { name: "Disparos / Filas", latency: 57 },
];

export function StatusMonitor() {
  const now = useNow();
  const online = useOnlineUsers();
  const history = useHistory();
  const time = now ? now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "--:--:--";
  const date = now ? now.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" }) : "—";
  const onlineFmt = online != null ? online.toLocaleString("pt-BR") : "—";
  const peak = history.length ? Math.max(...history.map((p) => p.v)).toLocaleString("pt-BR") : "—";

  return (
    <>
      <style>{`
        @keyframes svg-pulse-soft { 0%,100% { opacity: 0.5; transform: scale(1); } 50% { opacity: 1; transform: scale(1.04); } }
        @keyframes svg-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
      `}</style>

      <div className="relative overflow-hidden rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-50 via-white to-blue-50 p-5 backdrop-blur-xl shadow-[0_20px_60px_-25px_oklch(0.55_0.20_160/0.45)]">
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />
        <div
          aria-hidden
          className="pointer-events-none absolute -top-px left-0 h-px w-1/3"
          style={{
            background: "linear-gradient(90deg, transparent, oklch(0.85 0.18 150 / 0.9), transparent)",
            backgroundSize: "200% 100%",
            animation: "svg-shimmer 3.6s linear infinite",
          }}
        />
        <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-emerald-500/12 blur-3xl" />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <PingDot />
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-800">
              Todos os sistemas operacionais
            </span>
          </div>
          <div className="flex items-center gap-2 rounded-full border border-ink/10 bg-ink/[0.04] px-3 py-1 font-mono text-[12px] tabular-nums text-foreground/90">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="text-emerald-800">
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
            <span className="text-emerald-800">{time}</span>
            <span className="text-muted-foreground">·</span>
            <span className="capitalize text-muted-foreground">{date}</span>
          </div>
        </div>

        <div className="mt-5 grid gap-5 md:grid-cols-[1.1fr_1fr]">
          <div className="rounded-xl border border-ink/10 shadow-[var(--shadow-soft)] bg-ink/[0.04] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Usuários online agora</p>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="font-display text-3xl font-bold tabular-nums text-foreground md:text-4xl">
                    {onlineFmt}
                  </span>
                  <span className="text-xs text-emerald-800">ao vivo</span>
                </div>
              </div>
              <div className="flex flex-col items-end">
                <div className="flex items-center gap-1 rounded-full bg-emerald-500/12 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 ring-1 ring-emerald-500/30">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" style={{ animation: "svg-pulse-soft 1.6s ease-in-out infinite" }} />
                  estável
                </div>
                <span className="mt-1 text-[10px] text-muted-foreground">uptime 99,98%</span>
              </div>
            </div>
            <div className="mt-3">
              <LineChart data={history} />
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
              <span>últimas 12h · atualiza a cada 30 min</span>
              <span className="tabular-nums">pico · {peak}</span>
            </div>
          </div>

          <div className="rounded-xl border border-ink/10 shadow-[var(--shadow-soft)] bg-ink/[0.04] p-4">
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Serviços monitorados</p>
              <span className="text-[10px] text-muted-foreground">estabilidade · latência</span>
            </div>
            <ul className="mt-3 space-y-2">
              {SERVICES.map((s, i) => (
                <li key={s.name} className="flex items-center justify-between gap-3 text-[12.5px]">
                  <span className="flex min-w-0 items-center gap-2 text-foreground/90">
                    <span
                      className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"
                      style={{ animation: `svg-pulse-soft 1.6s ease-in-out ${i * 180}ms infinite` }}
                    />
                    <span className="truncate">{s.name}</span>
                  </span>
                  <span className="flex items-center gap-2 font-mono tabular-nums text-muted-foreground">
                    <ServiceSpark />
                    <span>{s.latency}ms</span>
                    <span className="text-emerald-800">●</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="mt-4 text-center text-[11px] text-muted-foreground">
          Monitoramento em tempo real · sem interrupções nas últimas 24h
        </p>
      </div>
    </>
  );
}